import React from 'react';
import { Redirect, RouteComponentProps } from 'react-router-dom';

const NEW_BASE_SEGMENT = '/digital-signage';

// Plain object literals inherit from Object.prototype, so a bracket lookup with an
// attacker-controlled key (e.g. a URL segment literally named "constructor" or "toString")
// can resolve to an inherited function instead of `undefined`. Object.create(null) has no
// prototype chain, so the same lookup safely misses instead.
function withoutPrototype<T extends Record<string, unknown>>(map: T): T {
  return Object.assign(Object.create(null), map);
}

// One entry per legacy URL prefix this section has had, each with its own old-sub-segment
// -> new-sub-segment map (the mapping isn't always the identity function -- "boards" became
// "menu-boards" back when this was renamed from /menu-boards to /digital-displays).
//
// - /menu-boards*: the original pre-ECOMM-13331 paths.
// - /digital-displays*: the brief "Digital Displays" rename (ECOMM-13331) that came before
//   this section settled on "Digital Signage" -- never released widely, but redirected
//   anyway in case it was linked/bookmarked during that window.
const LEGACY_PREFIX_SECTION_MAPS: Record<string, Record<string, string>> = withoutPrototype({
  '/enterprise/:enterpriseId/menu-boards': withoutPrototype({
    boards: 'menu-boards',
    playlists: 'playlists',
    displays: 'displays',
  }),
  '/enterprise/:enterpriseId/digital-displays': withoutPrototype({
    'menu-boards': 'menu-boards',
    playlists: 'playlists',
    displays: 'displays',
  }),
});

export const LEGACY_DIGITAL_SIGNAGE_PATHS = Object.keys(LEGACY_PREFIX_SECTION_MAPS);

/**
 * Bookmarked/shared links to this section's old URLs (see LEGACY_PREFIX_SECTION_MAPS above)
 * land here instead of 404ing. Registered as its own <Route> in App.tsx -- with `path` set
 * to LEGACY_DIGITAL_SIGNAGE_PATHS -- above the catch-all /enterprise/:enterpriseId route, so
 * it takes priority over EnterpriseAdmin for anything still under an old prefix.
 */
export function LegacyDigitalSignageRedirect(props: RouteComponentProps<{ enterpriseId: string }>): JSX.Element {
  const { match, location } = props;
  const { enterpriseId } = match.params;
  const newBase = `/enterprise/${enterpriseId}${NEW_BASE_SEGMENT}`;

  // `match.path` is the specific pattern (from the `path` array passed to <Route>) that
  // matched this URL; `match.url` is that pattern resolved against the actual URL, i.e. the
  // real old-prefix string to strip off, e.g. "/enterprise/ent-1/menu-boards". The `?? {}`
  // fallback can't itself be prototype-polluted for the lookup below since `sectionMap` is
  // only ever read with a bracket index, never spread/iterated -- but it's built the same
  // prototype-free way as everything else here for consistency.
  const sectionMap: Record<string, string> = LEGACY_PREFIX_SECTION_MAPS[match.path] ?? withoutPrototype({});
  const oldBase = match.url;
  const [, oldSection, ...restSegments] = location.pathname.slice(oldBase.length).split('/');
  const newSection = oldSection ? sectionMap[oldSection] : undefined;

  // Unrecognized sub-path (or none) under the old prefix -- land on the new Overview page
  // rather than guess at a target that might not exist.
  const target = newSection ? `${newBase}/${[newSection, ...restSegments].filter(Boolean).join('/')}` : newBase;

  return <Redirect to={`${target}${location.search}`} />;
}

export default LegacyDigitalSignageRedirect;
